import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  Button, 
  IconButton, 
  Box, 
  Typography,
  Alert,
} from '@mui/material';
import { MdClose, MdOpenInNew, MdArrowBack } from 'react-icons/md';
import { primaryColor } from './FAQSearch.style';

const LinkModal = ({ url, title, open, onClose }) => {
  const [overlayWindow, setOverlayWindow] = useState(null);
  const [windowClosed, setWindowClosed] = useState(false);
  const inactivityTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const INACTIVITY_TIMEOUT = 60000; // 1 minute

  const handleAutoClose = useRef(() => {
    console.log('Auto-closing due to inactivity...');
    
    // Close popup window
    if (overlayWindow && !overlayWindow.closed) {
      try {
        overlayWindow.close();
      } catch (e) {
        console.error('Failed to close popup:', e);
      }
    }
    
    // Reset state
    setOverlayWindow(null);
    setWindowClosed(false);
    onClose();
    
    // Reload page after state cleanup
    setTimeout(() => {
      window.location.reload();
    }, 100);
  });

  const resetInactivityTimer = useRef(() => {
    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    

    // Start new 10-second timer
    inactivityTimerRef.current = setTimeout(() => {
      
      // Start countdown
      let secondsLeft = 10;
      countdownIntervalRef.current = setInterval(() => {
        secondsLeft -= 1;
        
        if (secondsLeft <= 0) {
          clearInterval(countdownIntervalRef.current);
          handleAutoClose.current();
        }
      }, 1000);
    }, INACTIVITY_TIMEOUT);
  });

  // Monitor overlay window
  useEffect(() => {
    if (overlayWindow && !overlayWindow.closed) {
      // Check if window is closed every 500ms
      const checkClosed = setInterval(() => {
        if (overlayWindow.closed) {
          console.log('Popup closed by user');
          setWindowClosed(true);
          setOverlayWindow(null);
          clearInterval(checkClosed);
          window.focus();
          resetInactivityTimer.current();
        }
      }, 500);

      // Monitor popup window for activity
      const monitorPopupActivity = setInterval(() => {
        if (overlayWindow && !overlayWindow.closed) {
          try {
            if (overlayWindow.document) {
              const popupDoc = overlayWindow.document;
              const resetTimer = () => {
                console.log('Popup activity detected');
                resetInactivityTimer.current();
              };
              
              popupDoc.addEventListener('click', resetTimer);
              popupDoc.addEventListener('keydown', resetTimer);
              popupDoc.addEventListener('scroll', resetTimer);
              popupDoc.addEventListener('mousemove', resetTimer);
              
              clearInterval(monitorPopupActivity);
            }
          } catch (e) {
            // Cross-origin - can't access popup directly
          }
        }
      }, 100);

      // Start inactivity timer when window opens
      resetInactivityTimer.current();

      return () => {
        clearInterval(checkClosed);
        clearInterval(monitorPopupActivity);
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      };
    }
  }, [overlayWindow]);

  // Detect user activity in main window
  useEffect(() => {
    if (open && overlayWindow) {
      const handleActivity = () => {
        console.log('Main window activity detected - resetting timer');
        resetInactivityTimer.current();
      };

      window.addEventListener('click', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('touchstart', handleActivity);
      window.addEventListener('scroll', handleActivity);

      return () => {
        window.removeEventListener('click', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('touchstart', handleActivity);
        window.removeEventListener('scroll', handleActivity);
      };
    }
  }, [open, overlayWindow]);

  const openInOverlay = () => {
    const width = window.innerWidth * 0.5;
    const height = window.innerHeight * 0.5;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    const newWindow = window.open(
      url,
      "linkpopup",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=yes,status=no,resizable=yes,scrollbars=yes`
    );

    if (newWindow) {
      setOverlayWindow(newWindow);
      setWindowClosed(false);
      newWindow.focus();
    } else {
      alert('Please allow pop-ups for this site to open external resources.');
    }
  };

  const handleClose = () => {
    if (overlayWindow && !overlayWindow.closed) {
      overlayWindow.close();
    }
    // Clear all timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    setWindowClosed(false);
    setOverlayWindow(null);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {title || 'External Resource'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box textAlign="center" py={2}>
          {windowClosed && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Welcome back! Ready to continue? 👋
            </Alert>
          )}

          {!overlayWindow || overlayWindow.closed ? (
            <>
              <Typography variant="body1" mb={3} color="text.secondary">
                This resource will open in a new window. <br/> When you're done, close the window to return here.
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={openInOverlay}
                startIcon={<MdOpenInNew />}
                sx={{ 
                  mb: 2,
                  backgroundColor: primaryColor,
                  '&:hover': {
                    backgroundColor: primaryColor,
                    opacity: 0.9
                  }
                }}
              >
                Open Resource
              </Button>

              <Typography 
                variant="caption" 
                display="block" 
                color="text.secondary"
                sx={{ 
                  wordBreak: 'break-all',
                  mt: 2,
                  p: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 1
                }}
              >
                {url}
              </Typography>
            </>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                Resource opened in new window
              </Alert>
              
              <Typography variant="body2" mb={2}>
                The external resource is now open. Interact with either window to prevent auto-close.
              </Typography>

              <Button
                variant="outlined"
                onClick={handleClose}
                startIcon={<MdArrowBack />}
              >
                Back to Assistant
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LinkModal;